import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded"
import PowerIcon from "@mui/icons-material/Power"
import {
    Accordion,
    Stack,
    Table,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { useConfirm } from "material-ui-confirm"
import { ReactElement, useState } from "react"
import { useNavigate } from "react-router-dom"

import { DeviceSupersededDisplay } from "./deviceSupersededDisplay"
import HeaderBanner from "./headerBanner"
import { AlertDisplayComponent } from "../../components/alert/alertDisplay"
import DownloadDeviceAttachmentButton from "../../components/buttons/downloadDeviceAttachmentButton"
import { CommentsCardComponent } from "../../components/comments/commentsCard"
import {
    AppAccordion,
    AppAccordionDetails,
    AppAccordionSummary,
} from "../../components/dataDisplay/accordions/appAccordions"
import NestedVerticalKeyValueList from "../../components/dataDisplay/nestedVerticalKeyValueList"
import DeviceAttachmentLoadingRow from "../../components/loading/skeletons/deviceAttachmentLoadingRow"
import SingleDeviceSkeleton from "../../components/loading/skeletons/singleDeviceSkeleton"
import StatusAlert from "../../components/pills/status/statusAlert"
import AssessDeviceForm from "../../components/popUps/assessDevice/assessDeviceForm"
import AlertResponseForm from "../../components/popUps/raiseAlert/raiseAlertForm"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import AppTableContentRow from "../../components/tables/appTableContentRow"
import { Datasets } from "../../constants/enums"
import { DRAFT_AMENDMENT, DRAFT_DEVICE } from "../../constants/generalConstants"
import useGetHeaderActions from "../../hooks/useGetHeaderActions"
import { useGetLctTtrRbac } from "../../hooks/useGetLctTtrRbac"
import { SINGLE_GEN_DEVICE_DRAFT_NAV_LINK } from "../../router/routes"
import useDeleteAlert from "../../services/middleware/hooks/deviceRegisters/useDeleteAlert"
import useDeleteDevice from "../../services/middleware/hooks/deviceRegisters/useDeleteDevice"
import useUploadedDeviceAttachments from "../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import {
    ISingleDevice,
    IStandard,
} from "../../services/middleware/mappers/singleDeviceMapper"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import {
    getAmendDeviceUrl,
    getContinueDraftAmendmentUrl,
    getContinueDraftversionUrl,
    getNewVersionUrl,
} from "../../utils/routes/routeUtils"

export const SUPPORTING_DOCS_HEADER = "Supporting Documents"
export const SUPPORTING_DOCS_SUBHEADER =
    "This section displays any supporting documentation that has been uploaded by the manufacturer for this device. Please do not distribute downloaded content and delete it as soon as you no longer require it"

const STANDARD_COMPLY = "Y"
interface ISingleGenDeviceDisplayProps {
    data: ISingleDevice
    mutate: () => void
    isLoading: boolean
    isValidating: boolean
    isDraft?: boolean
}

/**
 * Returns webpage for single generation device summary page. This is a display
 * only component and does not handle API calling / logic
 * @example
 * < SingleGenDeviceDisplay />
 */
const SingleGenDeviceDisplay = ({
    data,
    mutate,
    isLoading,
    isValidating,
    isDraft,
}: ISingleGenDeviceDisplayProps): ReactElement => {
    const [isAlertFormOpen, setIsAlertFormOpen] = useState(false)
    const [isAssessmentFormOpen, setIsAssessmentFormOpen] = useState(false)
    const confirm = useConfirm()
    const { deleteResponse } = useDeleteAlert()
    const { deleteGenDevice, isDeleting } = useDeleteDevice()
    const navigate = useNavigate()

    const {
        alert,
        supersededDeviceRef,
        publishedDate,
        status,
        draftAmendmentRef,
        draftVersionRef,
        deviceRef,
        attachmentUuid,
        isSubscribed,
    } = data.header

    const dataset = isDraft ? Datasets.TTR_DRAFT : Datasets.TTR

    const { canDeleteAlert, canReadComments, canReadAlerts } = useGetLctTtrRbac(
        data.header.deviceRef,
        dataset
    )

    const {
        uploadedAttachments,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
        error: uploadedAttachmentsError,
    } = useUploadedDeviceAttachments(dataset, deviceRef, attachmentUuid)

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !uploadedAttachmentsError &&
        !!uploadedAttachments

    const isDraftDevice = status === DRAFT_DEVICE
    const isDraftAmendment = status === DRAFT_AMENDMENT

    const isNotADraft = !isDraftAmendment && !isDraftDevice

    const deleteAlert = (): void => {
        confirm({
            description:
                "This will permanently remove the alert on device " +
                `${deviceRef}`,
        })
            .then(async () => {
                deleteResponse(deviceRef).then(mutate)
            })
            .catch(() => {
                // ...
            })
    }

    const onClickRaiseAlert = (): void => setIsAlertFormOpen(true)

    const onClickAssessDevice = (): void => setIsAssessmentFormOpen(true)

    const onClickAmendDevice = (): void =>
        navigate(getAmendDeviceUrl(dataset, deviceRef))

    const onClickContinueDraftAmendment = (): void =>
        navigate(getContinueDraftAmendmentUrl(dataset, deviceRef))

    const onClickContinueDraftversion = (): void =>
        navigate(getContinueDraftversionUrl(Datasets.TTR_DRAFT, deviceRef))

    const onClickNewVersion = (): void =>
        navigate(getNewVersionUrl(dataset, deviceRef))

    const onClickViewDraftAmendment = (): void =>
        navigate(SINGLE_GEN_DEVICE_DRAFT_NAV_LINK(draftAmendmentRef ?? ""))

    const onClickViewDraftVersion = (): void =>
        navigate(SINGLE_GEN_DEVICE_DRAFT_NAV_LINK(draftVersionRef ?? ""))

    const onClickDeleteDevice = (): Promise<void> =>
        confirm({
            description: `This will permanently remove the device ${deviceRef}`,
        }).then(() => {
            deleteGenDevice(deviceRef)
        })

    const headerActions = useGetHeaderActions(data, dataset, {
        onClickRaiseAlert,
        onClickAssessDevice,
        onClickAmendDevice,
        onClickContinueDraftAmendment,
        onClickContinueDraftversion,
        onClickNewVersion,
        onClickViewDraftAmendment,
        onClickViewDraftVersion,
        onClickDeleteDevice,
    })

    if (isDeleting) {
        return <SingleDeviceSkeleton />
    }

    return (
        <>
            <Grid container spacing={GRID_SPACING}>
                <Grid xs={12}>
                    <Stack spacing={GRID_SPACING}>
                        <HeaderBanner
                            title={deviceRef}
                            subtitle={data.header.manufacturer}
                            subSubtitle="Generation Device"
                            actions={headerActions}
                            Icon={<PowerIcon />}
                            isValidating={isValidating}
                        />
                        {alert && canReadAlerts && (
                            <AlertDisplayComponent
                                canDeleteAlerts={canDeleteAlert(
                                    alert?.alerterUsername
                                )}
                                alert={alert}
                                deleteAlert={deleteAlert}
                            />
                        )}
                        {supersededDeviceRef && (
                            <DeviceSupersededDisplay
                                supersededDeviceRef={supersededDeviceRef}
                                latestDeviceRef={deviceRef}
                            />
                        )}
                    </Stack>
                </Grid>
                <Grid md={6} xs={12}>
                    <Stack spacing={GRID_SPACING}>
                        <AppCard>
                            <AppCardHeader
                                title="Device Status"
                                subheader="This is the current status of the device. It is assigned by ENA appointed evaluators"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent>
                                <Stack spacing={GRID_SPACING}>
                                    <StatusAlert
                                        status={data?.header?.status}
                                    />
                                </Stack>
                            </AppCardContent>
                        </AppCard>
                        <AppCard>
                            <NestedVerticalKeyValueList
                                nestedKwargs={data?.content}
                            />
                        </AppCard>
                    </Stack>
                </Grid>
                <Grid md={6} xs={12}>
                    <Stack spacing={GRID_SPACING}>
                        <AppCard>
                            <AppCardHeader
                                title="Compliance Information"
                                subheader="This section displays the standards the device was assessed against."
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <AppCardContent>
                                <Accordion>
                                    <AppAccordionSummary>
                                        <Typography fontWeight="500">
                                            Compliance with GB Standards
                                        </Typography>
                                    </AppAccordionSummary>
                                    <AppAccordionDetails>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow hover>
                                                    <TableCell>
                                                        Standard
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        Fully Type tested
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        Partially Type tested
                                                    </TableCell>
                                                </TableRow>
                                                {data?.standards?.GB.map(
                                                    (standard: IStandard) => (
                                                        <AppTableContentRow
                                                            key={
                                                                standard.standard
                                                            }
                                                            hover
                                                        >
                                                            <TableCell>
                                                                {
                                                                    standard.standard
                                                                }
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {standard.fullTT ===
                                                                    STANDARD_COMPLY && (
                                                                    <FiberManualRecordRoundedIcon />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {standard.partialTT ===
                                                                    STANDARD_COMPLY && (
                                                                    <FiberManualRecordRoundedIcon />
                                                                )}
                                                            </TableCell>
                                                        </AppTableContentRow>
                                                    )
                                                )}
                                            </TableHead>
                                        </Table>
                                    </AppAccordionDetails>
                                </Accordion>
                                <AppAccordion>
                                    <AppAccordionSummary>
                                        <Typography fontWeight="500">
                                            Compliance with NI Standards
                                        </Typography>
                                    </AppAccordionSummary>
                                    <AppAccordionDetails>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow hover>
                                                    <TableCell>
                                                        Standard
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        Fully Type tested
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        Partially Type tested
                                                    </TableCell>
                                                </TableRow>
                                                {data?.standards?.NI.map(
                                                    (standard: IStandard) => (
                                                        <AppTableContentRow
                                                            key={
                                                                standard.standard
                                                            }
                                                            hover
                                                        >
                                                            <TableCell>
                                                                {
                                                                    standard.standard
                                                                }
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {standard.fullTT ===
                                                                    STANDARD_COMPLY && (
                                                                    <FiberManualRecordRoundedIcon />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {standard.partialTT ===
                                                                    STANDARD_COMPLY && (
                                                                    <FiberManualRecordRoundedIcon />
                                                                )}
                                                            </TableCell>
                                                        </AppTableContentRow>
                                                    )
                                                )}
                                            </TableHead>
                                        </Table>
                                    </AppAccordionDetails>
                                </AppAccordion>
                            </AppCardContent>
                        </AppCard>
                        <AppCard>
                            <AppCardHeader
                                title={SUPPORTING_DOCS_HEADER}
                                subheader={SUPPORTING_DOCS_SUBHEADER}
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                            />
                            <Table size="small">
                                <TableHead>
                                    <TableRow hover>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell align="center">
                                            Restricted
                                        </TableCell>
                                        <TableCell align="center" />
                                    </TableRow>
                                    {isLoadingUploadedAttachments && (
                                        <>
                                            <DeviceAttachmentLoadingRow />
                                            <DeviceAttachmentLoadingRow />
                                            <DeviceAttachmentLoadingRow />
                                        </>
                                    )}
                                    {uploadedAttachmentsAreValid && (
                                        <>
                                            {uploadedAttachments.length ===
                                                0 && (
                                                <AppTableContentRow>
                                                    <TableCell colSpan={4}>
                                                        No supporting documents
                                                        have been uploaded for
                                                        this device
                                                    </TableCell>
                                                </AppTableContentRow>
                                            )}
                                            {uploadedAttachments.map(
                                                (summary) => (
                                                    <AppTableContentRow
                                                        key={summary.fileName}
                                                        hover
                                                    >
                                                        <TableCell>
                                                            {summary.fileName}
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                summary.description
                                                            }
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {summary.private && (
                                                                <CheckCircleIcon color="success" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <DownloadDeviceAttachmentButton
                                                                deviceSysRef={
                                                                    data.header
                                                                        .deviceRef
                                                                }
                                                                fileName={
                                                                    summary.fileName
                                                                }
                                                                dataset={
                                                                    dataset
                                                                }
                                                                attachmentUuid={
                                                                    data.header
                                                                        .attachmentUuid
                                                                }
                                                                isAmendment={
                                                                    isDraftAmendment
                                                                }
                                                            />
                                                        </TableCell>
                                                    </AppTableContentRow>
                                                )
                                            )}
                                        </>
                                    )}
                                </TableHead>
                            </Table>
                        </AppCard>

                        {canReadComments && isNotADraft && (
                            <CommentsCardComponent
                                deviceSystemRef={deviceRef}
                                dataset={Datasets.TTR}
                                comments={data.header.comments}
                                mutate={mutate}
                                pinnedCommentId={data.header.pinnedComment}
                                fetchingComments={isLoading}
                                isSubscribed={isSubscribed}
                                isThreadUnread={data.header.isThreadUnread}
                                lastReadAt={data.header.lastReadAt}
                                lastReadBy={data.header.lastReadBy}
                            />
                        )}
                    </Stack>
                </Grid>
            </Grid>
            <AlertResponseForm
                deviceSystemRef={deviceRef}
                isOpen={isAlertFormOpen}
                setIsOpen={setIsAlertFormOpen}
                mutate={mutate}
            />
            <AssessDeviceForm
                deviceRef={deviceRef}
                deviceStatus={data.header.status}
                basedOnPublishedDate={publishedDate}
                isOpen={isAssessmentFormOpen}
                setIsOpen={setIsAssessmentFormOpen}
                mutate={mutate}
                mode="TTR"
            />
        </>
    )
}

SingleGenDeviceDisplay.defaultProps = {
    isDraft: false,
}

export default SingleGenDeviceDisplay
